import { Plus, X } from 'lucide-react';

import { normalizePrizePool } from '../../Public/tournament/tournamentHelpers';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { NativeSelect } from '../../ui/select-native';

const EMPTY_CONTRIBUTOR = { name: '', amount: '', currency: '' };

const PrizePoolEditor = ({ value, onChange }) => {
  const normalized = normalizePrizePool(value);
  const pool = {
    ...normalized,
    ...(value && typeof value === 'object' ? value : {}),
    contributors:
      value && typeof value === 'object' && Array.isArray(value.contributors)
        ? value.contributors
        : normalized.contributors,
  };
  const patch = (key, next) => onChange?.({ ...pool, [key]: next });
  const contributors = pool.contributors || [];

  const patchContributor = (index, key, next) => {
    const nextContributors = contributors.map((contributor, contributorIndex) =>
      contributorIndex === index ? { ...contributor, [key]: next } : contributor
    );
    patch('contributors', nextContributors);
  };

  return (
    <div className="grid gap-4 rounded-md border border-border/60 bg-background/40 p-5">
      <div className="grid gap-5 sm:grid-cols-[minmax(0,1fr)_10rem]">
        <label className="flex flex-col gap-2">
          <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
            Visibility
          </span>
          <NativeSelect
            value={pool.status}
            onChange={(event) => patch('status', event.target.value)}
          >
            <option value="none">No prize pool</option>
            <option value="undisclosed">Undisclosed</option>
            <option value="published">Published</option>
          </NativeSelect>
        </label>

        {pool.status === 'published' && (
          <label className="flex flex-col gap-2">
            <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
              Currency
            </span>
            <Input
              value={pool.currency || ''}
              maxLength={3}
              onChange={(event) => patch('currency', event.target.value.toUpperCase())}
              placeholder="USD"
              aria-label="Prize pool currency"
            />
          </label>
        )}
      </div>

      {pool.status === 'published' && (
        <label className="flex max-w-xs flex-col gap-2">
          <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
            Total prize pool
          </span>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={pool.amount ?? ''}
            onChange={(event) => patch('amount', event.target.value)}
            placeholder="0.00"
            aria-label="Total prize pool amount"
          />
        </label>
      )}

      {pool.status !== 'none' && (
        <div className="grid gap-3">
          <div>
            <div className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
              Contributors
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Credit the kzcomps or community partners that contributed to this pool.
            </p>
          </div>

          {contributors.map((contributor, index) => (
            <div
              key={`contributor-${index}`}
              className="grid gap-2 rounded-md border border-border/60 p-3 sm:grid-cols-[minmax(0,1fr)_8rem_5rem_auto] sm:items-end"
            >
              <label className="flex flex-col gap-1.5">
                <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  kzcomp / contributor
                </span>
                <Input
                  value={contributor.name || ''}
                  onChange={(event) => patchContributor(index, 'name', event.target.value)}
                  placeholder="KZCOMP"
                  aria-label={`Contributor ${index + 1} name`}
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  Amount
                </span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={contributor.amount ?? ''}
                  onChange={(event) => patchContributor(index, 'amount', event.target.value)}
                  placeholder="—"
                  aria-label={`Contributor ${index + 1} amount`}
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  Currency
                </span>
                <Input
                  value={contributor.currency || pool.currency || ''}
                  maxLength={3}
                  onChange={(event) =>
                    patchContributor(index, 'currency', event.target.value.toUpperCase())
                  }
                  placeholder={pool.currency || 'USD'}
                  aria-label={`Contributor ${index + 1} currency`}
                />
              </label>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() =>
                  patch(
                    'contributors',
                    contributors.filter((_, contributorIndex) => contributorIndex !== index)
                  )
                }
                aria-label={`Remove contributor ${index + 1}`}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}

          <Button
            type="button"
            variant="outline"
            className="w-fit"
            onClick={() => patch('contributors', [...contributors, { ...EMPTY_CONTRIBUTOR }])}
          >
            <Plus className="h-4 w-4" />
            Add contributor
          </Button>
        </div>
      )}

      {pool.status === 'undisclosed' && (
        <p className="text-xs text-muted-foreground">
          The public page will show that a prize pool exists without revealing the amount.
        </p>
      )}
    </div>
  );
};

export default PrizePoolEditor;
